/**
 * @file ticket-booking-bll.service.ts
 * @description This service handles the business logic for ticket booking in the visitor dashboard application.
 * It interacts with the API service, manages local storage, and provides utility methods for ticket operations.
 *
 * @author Jyoti
 * @date 19 August 2025
 */
import { Injectable } from "@angular/core";
import { TicketBookingApiService } from "./ticket-booking-api.service";
import { HttpResult } from "../../core/services/base-http.service";
import {
  APIErrorModel,
  BaseResponse,
} from "../../shared/models/base-response.model";
import {
  TicketBookingModel,
  VerifyRazorpayPaymentRequest,
  VerifyRazorpayPaymentResponse,
  initiatePaymentResModel,
  registerTypeModel,
  registerTypeReqBodyModel,
  registerTypeResModel,
  registrationDetailsResModel,
} from "./ticket-booking.model";
import { firstValueFrom } from "rxjs";
import { StorageService } from "../../core/services/storage.service";
import { Router } from "@angular/router";
import { LoaderService } from "../../shared/components/loader/loader.service";
import { ProfileBllService } from "../profile/profile-bll.service";
import { UserEventStatusRespModel } from "../profile/profile.model";
import { UserService } from "../../core/services/user.service";
import { RazorpayService } from "../../core/services/razorpay.service";
declare var Razorpay: any;
@Injectable({
  providedIn: "root",
})
export class TicketBookingBllService {
  /**
   * @constructor
   * @param api - Service for API interactions related to ticket booking.
   * @param router - Angular Router for navigation.
   * @param storageService - Service for managing local storage.
   */
  constructor(
    private api: TicketBookingApiService,
    private router: Router,
    private storageService: StorageService,
    private loaderService: LoaderService,
    private profileBllService: ProfileBllService,
    private userService: UserService,
    private razorpaySdk: RazorpayService

  ) { }

  total: number = 0; // Total amount for selected tickets.
  submitBtnDisabled: boolean = true; // Flag to disable/enable the submit button.

  /**
   * Fetches the event registration types from the API.
   * @returns {Promise<BaseResponse<TicketBookingModel>>} The event registration types.
   * @throws Error if the API call fails.
   */
  async getEventRegisterType() {
    const result: HttpResult<BaseResponse<TicketBookingModel>> =
      await firstValueFrom(this.api.getEventRegisterType());

    const { status, body } = result;

    if (status === 200) {
      // Return data if status is OK
      let discounts = this.extractUserDiscounts(body);
      this.storageService.saveDiscounts(JSON.stringify(discounts));
      return body;
    } else {
      // Throw error with status and message if request fails
      throw <APIErrorModel>{
        status,
        message: body.message
      };
    }
    // return result;
  }

  /**
   * Extracts the register types from the API response.
   * @param result - The API response.
   * @returns {any[]} The list of register types.
   */
  extractRegisterTypes(result: any): any[] {
    return result?.data?.register_types || [];
  }

  /**
   * Extracts user discounts from the API response.
   * @param result - The API response.
   * @returns {any[]} The list of user discounts.
   */
  extractUserDiscounts(result: any): any[] {
    return (
      result?.data?.user_discounts || [
        {
          code: "",
          discount_amount: "",
          display_name: "",
          id: "",
        },
      ]
    );
  }

  /**
   * Formats the ticket data by adding default properties.
   * @param data - The ticket data.
   * @returns {any[]} The formatted ticket data.
   */
  formatTickets(data: any[]): any[] {
    return Array.isArray(data)
      ? data.map((ticket) => ({ ...ticket, count: 0, isSelected: false }))
      : [];
  }

  /**
   * Merges stored tickets with the current tickets.
   * @param currentTickets - The current tickets.
   * @param storedTickets - The stored tickets.
   * @returns {registerTypeModel[]} The merged ticket data.
   */
  mergeStoredTickets(
    currentTickets: registerTypeModel[],
    storedTickets: any[]
  ): registerTypeModel[] {
    return currentTickets.map((ticket) => {
      const stored = storedTickets.find(
        (t: any) => t.event_register_type_id === ticket.id
      );
      return stored
        ? { ...ticket, count: stored.register_people_count, isSelected: true }
        : ticket;
    });
  }

  /**
   * Increments the ticket count safely.
   * @param ticket - The ticket to increment.
   */
  incrementTicketCount(ticket: any): void {
    if (ticket.count < ticket.max_ticket_for_order) {
      ticket.count++;
      ticket.isSelected = true;
    }
  }

  /**
   * Calculates the total amount for the selected tickets.
   * @param tickets - The selected tickets.
   * @returns {number} The total amount.
   */
  calculateTotal(tickets: registerTypeModel[]): number {
    return tickets.reduce(
      (sum, ticket) => sum + ticket.paid_amount * ticket.count,
      0
    );
  }

  /**
   * Decrements the ticket count safely.
   * @param ticket - The ticket to decrement.
   */
  decrementTicketCount(ticket: any): void {
    if (ticket.count > 0) {
      ticket.count--;
      if (ticket.count === 0) {
        ticket.isSelected = false;
      }
    }
  }

  /**
   * Handles changes in ticket details and updates local storage.
   * @param result - The result containing ticket details.
   * @param selectedTickets - The selected tickets.
   */

  handleTicketDetailsChange(
    selectedTickets: registerTypeModel[],
    result?: any
  ) {
    // Store the selected tickets in local storage for summary
    let summaryId = result?.data?.summary_id;

    this.storageService.saveRegisterTypeSelected(
      JSON.stringify(selectedTickets)
    );
    // Store the summary ID in local storage
    if (summaryId !== undefined && summaryId !== null) {
      this.storageService.saveSummaryId(summaryId);
    }
    // Navigate to ticket summary page
    setTimeout(() => {
      this.router.navigate(["reg/ticket-summary"]);
      this.loaderService.hide();
    }, 1000)

  }

  /**
   * Retrieves the summary details from local storage.
   * @returns {any[]} The stored ticket details or an empty array if not found.
   */
  getSummaryDetails() {
    this.loaderService.show();
    let storedTickets = this.storageService.getRegisterTypeSelected();
    if (storedTickets) {
      this.loaderService.hide();
      // return storedTickets;
      storedTickets = storedTickets.map((ticket: any) => ({
        ...ticket,
        display_price: ticket.display_price * ticket.count,
        paid_amount: ticket.paid_amount * ticket.count,
      }));
      return storedTickets || [];
    } else {
      this.loaderService.hide();
      console.warn("No ticket details found in local storage.");
      return [];
    }
  }
  /**
   * Retrieves user discounts from local storage.
   * @returns {any[]} The stored user discounts or an empty array if not found.
   */
  getUserDiscounts() {
    try {
      const discountsString = this.storageService.getDiscounts();

      if (!discountsString) {
        console.warn("No discounts found in local storage.");
        return [];
      }

      const discounts = JSON.parse(discountsString);
      // Ensure discounts is an array
      if (!Array.isArray(discounts)) {
        console.warn("Discounts data is not an array:", discounts);
        return [];
      }
      // Return the discounts or an empty array if undefined
      if (discounts.length === 0) {
        console.warn("No discounts available.");
        return [];
      }
      this.loaderService.hide();
      console.log("Retrieved discounts:", discounts);

      return discounts || [];
    } catch (error) {
      console.error("Error parsing discounts from local storage:", error);
      return [];
    }
  }

  /**
   * Confirms the booking by initiating payment.
   * @returns {Promise<any>} The payment initiation response.
   * @throws Error if the booking details are incomplete or the API call fails.
   */
  async confirmBooking() {
    const summaryId = this.storageService.getSummaryId();
    // const selectedTickets = this.storageService.getRegisterTypeSelected();

    if (!summaryId) {
      throw new Error("Booking details are incomplete.");
    }

    return firstValueFrom(this.api.initiatePayment(summaryId))
      .then((result: HttpResult<BaseResponse<initiatePaymentResModel>>) => {
        const { status, body } = result;

        if (status === 200) {
          // Handle successful payment initiation
          return body.data;
        } else if (status === 409) {
          // Handle conflict error when payment is already in progress/authorized        
          throw new Error("Payment already initiated, please wait for 15 minutes to initiate again");
        } else {
          // Handle other error responses
          throw new Error(status.toString() + ":" + body.message);
        }
      })
      .catch((error) => {
        console.error("Error confirming booking:", error);
        throw new Error("Failed to confirm booking: " + error.message);
      });
  }

  /**
   * Fetches registration details using the summary ID.
   * @returns {Promise<any>} The registration details.
   * @throws Error if the summary ID is not available or the API call fails.
   */
  getRegistrationDetails(summaryId: string) {
    if (!summaryId) {
      throw new Error(
        "Summary ID is not available. Please confirm booking first."
      );
    }
    // Ensure summaryId is not null or undefined
    if (!summaryId) {
      throw new Error("Summary ID is required to fetch registration details.");
    }
    // Call the API to get registration details
    // Use firstValueFrom to convert Observable to Promise
    return firstValueFrom(this.api.getRegistrationDetails(summaryId))
      .then((result: HttpResult<BaseResponse<registrationDetailsResModel>>) => {
        const { status, body } = result;
        if (status === 200) {
          this.storageService.savePaymentDiscount(
            body.data.details.discount_amount.toString()
          );
          // Handle successful response
          return body.data;
        } else {
          // Handle error response
          throw new Error(status.toString() + ":" + body.message);
        }
      })
      .catch((error) => {
        console.error("Error fetching registration details:", error);
        throw new Error(
          "Failed to fetch registration details: " + error.message
        );
      });
  }

  getSummaryIdFromStorage(): string | null {
    return this.storageService.getSummaryId();
  }

  /**
   * Submits the register types to the API.
   * @param requestBody - The request body containing register type details.
   * @returns {Promise<BaseResponse<registerTypeResModel>>} The response from the API.
   * @throws Error if the API call fails or returns an error status.
   */
  SubmitRegisterTypes(
    requestBody: registerTypeReqBodyModel
  ): Promise<BaseResponse<registerTypeResModel>> {
    const summaryId = this.storageService.getSummaryId();


    const request$ = summaryId
      ? this.api.updateRegisterType(requestBody, summaryId)
      : this.api.addRegisterType(requestBody);

    return firstValueFrom(request$)
      .then((result) => {
        const { status, body } = result;

        if (status === 200) {
          return body; // return consistent BaseResponse object
        } else {
          return Promise.reject({
            status,
            message: body.message || "Unknown error occurred",
          });
        }
      })
      .catch((error: APIErrorModel) => {
        console.error("Error submitting register types:", error);
        return Promise.reject({
          status: error.status || 500,
          message: error.message || "Failed to submit register types",
        });
      });
  }

  /**

   * Initiates payment using Razorpay.
   * @param data - The payment data.
   * @param onSuccess - Callback for successful payment.
   * @param onFailure - Callback for failed payment.
   */

  /**
   * Initiates payment using Razorpay.
   * @param name - Customer name.
   * @param mobile - Customer mobile number.
   * @param data - The payment data (order response from backend).
   * @param onSuccess - Callback for successful payment.
   * @param onFailure - Callback for failed/closed payment.
   */

  async payWithRazor(
    name: string,
    mobile: string,
    data: initiatePaymentResModel,
    onSuccess: (response: any) => void,
    onFailure: (error: any) => void
  ): Promise<void> {
    return this.razorpaySdk.openCheckout({
      order: data, // backend initiatePaymentResModel
      customer: { name, mobile },
      onSuccess,
      onFailure,
      themeColor: '#008940',
    });
  }
  /**
   * Verifies the Razorpay payment with the backend.
   */
  verifyPayment(payload: VerifyRazorpayPaymentRequest): Promise<string> {
    // Retrieve the summary ID from local storage.
    const summaryId = this.storageService.getSummaryId();

    // If summaryId is missing, throw an error immediately.
    if (!summaryId) {
      throw new Error("Summary ID is required to verify payment.");
    }

    return firstValueFrom(this.api.verifyRazorpayPayment(summaryId, payload))
      .then(
        (result: HttpResult<BaseResponse<VerifyRazorpayPaymentResponse>>) => {
          const { status, body } = result;

          if (status === 200) {
            // ✅ Clear Razorpay data after successful verification
            this.storageService.clearRazorpayData();
            this.storageService.savePaymentSuccessFlag("true");
            // Return success message (fallback provided)
            return body?.data?.message ?? "Operation succeeded";
          } else {
            // Non-200 handled as error
            throw new Error(
              `${status}:${body?.message || "Verification failed"}`
            );
          }
        }
      )
      .catch((error: any) => {
        // Prefer server message if present
        const serverMsg =
          error?.error?.message || error?.message || "Unknown error";
        throw new Error("Failed to verify payment: " + serverMsg);
      });
  }

  // Records payment failure for ticket booking.
  failurePayment(payload: any): Promise<string> {
    const summaryId = this.storageService.getSummaryId();

    // If summaryId is missing, throw an error immediately.
    if (!summaryId) {
      throw new Error("Summary ID is required to report failed payment.");
    }

    return firstValueFrom(this.api.paymentFailure(summaryId, payload))
      .then(
        (result: HttpResult<BaseResponse<any>>) => {
          const { status, body } = result;

          if (status === 200) {
            // Return success message (fallback provided)
            return body?.message ?? "Failure reported successfully";
          } else {
            // Non-200 handled as error
            throw new Error(
              `${status}:${body?.message || "Reporting failed"}`
            );
          }
        }
      )
      .catch((error: any) => {
        // Prefer server message if present
        const serverMsg =
          error?.error?.message || error?.message || "Unknown error";
        throw new Error("Failed to report payment failure: " + serverMsg);
      });
  }

  /** Fetch user profile and event status */
  fetchUserEventStatus() {
    this.profileBllService
      .submitGetUserEventStatus()
      .then((res: BaseResponse<UserEventStatusRespModel>) => {
        let userName =
          res.data.user_profile.first_name +
          " " +
          res.data.user_profile.last_name;
        this.userService.setUser({
          name: userName, // get from API or JWT
          profileImg: res.data.user_profile.image_url, // fallback if empty
          mobile: res.data.user_profile.mobile,
          state: res.data.user_profile.state,
          city: res.data.user_profile.city,
          hasUnassignedTickets: res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
          hasAssignedTickets: res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || res.data.event_my_tickets.tickets_assigned_for_me > 0,
        });

        this.storageService.saveUserImgUrl(res.data.user_profile.image_url);
        this.storageService.saveUserName(userName);
      })
      .catch((err: APIErrorModel) => {
        console.error("Error fetching user event status:", err);
        alert("Failed to fetch user event status. Please try again.");
      });
  }

  saveRefSource(refSource: string) {
    if (typeof refSource !== 'string' || refSource.trim().length === 0) {
      return;
    }
    this.storageService.saveRefSource(refSource);
  }

  getRefSource() {
    return this.storageService.getRefSource();
  }
}
